/**
 * MacroOpticsPanel -- read-only view of the last Macro Optics sync (Discovery page, 2026-10-07).
 *
 * Binds to GET /api/macro-optics/latest, which returns system_config key `macro_optic_sync`:
 *   { macro_optic_sync: { mode, last_sync_at, received_at,
 *       market_regime: { trend_strength, liquidity_profile, oil_trend_signal, yield_curve_signal },
 *       actionable_ideas: { longs[], shorts[], sector_calls[] } } }
 * Display only: nothing in the trading path reads this data. Every field is optional here so a partial or old row never breaks the page.
 */
import React, { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Globe2 } from 'lucide-react'
import { macroOptics } from '../lib/api'

const mono = { fontFamily: 'JetBrains Mono, monospace' }
const card = { background: '#111827', border: '1px solid #1f2937', borderRadius: '0.75rem' }

const REGIME = [
  ['trend_strength', 'Trend strength'],
  ['liquidity_profile', 'Liquidity'],
  ['oil_trend_signal', 'Oil trend'],
  ['yield_curve_signal', 'Yield curve'],
]

export function parseMacroOptics(resp) {
  const m = resp?.data?.macro_optic_sync
  if (!m || typeof m !== 'object') return null
  const list = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [])
  return {
    mode: m.mode || '-',
    lastSync: m.last_sync_at || null,
    receivedAt: m.received_at || resp?.updated_at || null,
    regime: m.market_regime && typeof m.market_regime === 'object' ? m.market_regime : {},
    longs: list(m.actionable_ideas?.longs),
    shorts: list(m.actionable_ideas?.shorts),
    sectors: list(m.actionable_ideas?.sector_calls),
  }
}

function Chips({ items, tone }) {
  if (!items.length) return <span className="text-slate-600 text-xs">none</span>
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((s) => (
        <span key={s} className={`text-xs px-2 py-0.5 rounded border ${tone}`} style={mono}>{s}</span>
      ))}
    </div>
  )
}

export default function MacroOpticsPanel() {
  const [view, setView] = useState(undefined) // undefined = loading, null = nothing stored
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    try {
      setErr(null)
      setView(parseMacroOptics(await macroOptics.latest()))
    } catch (e) {
      setErr(e?.message || 'failed to load')
      setView(null)
    }
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div className="p-4 mb-4" style={card}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-semibold tracking-widest" style={{ fontFamily: 'Rajdhani, sans-serif' }}>MACRO OPTICS</h2>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700" style={mono}>DISPLAY ONLY</span>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-200" aria-label="Refresh Macro Optics">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {err && <p className="text-xs text-red-400">Could not load Macro Optics: {err}</p>}
      {view === undefined && !err && <p className="text-xs text-slate-500">Loading...</p>}
      {view === null && !err && <p className="text-xs text-slate-500">No Macro Optics sync stored yet (POST /api/external/macro-optics-sync).</p>}
      {view && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500" style={mono}>
            mode {view.mode} · sender time {view.lastSync ? String(view.lastSync).slice(0, 19) : '-'} · received {view.receivedAt ? String(view.receivedAt).slice(0, 19) : '-'}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {REGIME.map(([k, label]) => (
              <div key={k} className="rounded-lg p-2.5" style={{ background: '#0b1220', border: '1px solid #1f2937' }}>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
                <p className="text-sm text-slate-200 mt-0.5">{view.regime[k] || '-'}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div><p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Longs</p><Chips items={view.longs} tone="border-emerald-700/40 text-emerald-300 bg-emerald-900/20" /></div>
            <div><p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Shorts</p><Chips items={view.shorts} tone="border-red-700/40 text-red-300 bg-red-900/20" /></div>
            <div><p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Sector calls</p><Chips items={view.sectors} tone="border-slate-600 text-slate-300 bg-slate-800/40" /></div>
          </div>
        </div>
      )}
    </div>
  )
}
